import { Icon } from '@iconify/react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useTextSelectionSearch } from '@context/TextSelectionSearchContext';
import { useExportTranscriptData } from '@hooks/postQueries';
import TagLibraryModal from './TagLibraryModal';
import TranscriptFilterByTag from './TranscriptFilterByTag';

const useStyles = makeStyles(() => ({
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const TranscriptListHeader = ({
  transcripts = [],
  selectedTags,
  onTagFilterChange,
  statusFilter,
  onStatusFilterChange,
  typeFilter,
  onTypeFilterChange,
  onUploadClick,
}) => {
  const classes = useStyles();
  const [openTagLibraryModal, setOpenTagLibraryModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('txt');
  const [scope, setScope] = useState('all');
  const { mutate: exportTranscripts, isPending: isDownloading } = useExportTranscriptData();

  const hasDerivedTranscripts = useMemo(() => transcripts.some((t) => t.is_derived), [transcripts]);

  const { openSearchModal } = useTextSelectionSearch();

  const { id } = useParams();

  const hasActiveFilters =
    (selectedTags && selectedTags.length > 0) || !!statusFilter || !!typeFilter;

  const closeExportModal = useCallback(() => {
    setExportModalOpen(false);
    setExportFormat('txt');
    setScope('all');
  }, []);

  const handleExportSuccess = useCallback(() => {
    closeExportModal();
  }, [closeExportModal]);

  const handleDownload = useCallback(() => {
    exportTranscripts({ studyId: id, exportFormat, scope }, { onSuccess: handleExportSuccess });
  }, [exportTranscripts, id, exportFormat, scope, handleExportSuccess]);

  return (
    <Box sx={{ py: 2, px: 1 }}>
      <Box className={classes.sectionHeader} sx={{ justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Transcripts</Typography>

        <Box>
          <Tooltip title={'Search transcripts with advanced filters'} arrow>
            <IconButton
              size="small"
              sx={{
                border: '2px solid #e8e8e8',
                '&:hover': { border: '2px solid #303030' },
                mr: 0.5,
              }}
              onClick={openSearchModal}
            >
              <Icon
                icon="material-symbols:search"
                style={{ width: '18px', height: '18px', color: '#303030' }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Filter transcripts'} arrow>
            <IconButton
              size="small"
              sx={{
                border: `2px solid ${hasActiveFilters || showFilters ? '#7D5AE8' : '#e8e8e8'}`,
                '&:hover': { border: '2px solid #303030' },
                mr: 0.5,
                bgcolor: hasActiveFilters ? 'rgba(125, 90, 232, 0.08)' : 'transparent',
              }}
              onClick={() => setShowFilters((prev) => !prev)}
            >
              <Icon
                icon="material-symbols:filter-list"
                style={{
                  width: '18px',
                  height: '18px',
                  color: hasActiveFilters || showFilters ? '#7D5AE8' : '#303030',
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Tag Library'} arrow>
            <IconButton
              LinkComponent={Link}
              size="small"
              sx={{
                border: '2px solid #e8e8e8',
                '&:hover': { border: '2px solid #303030' },
                mr: 0.5,
              }}
              onClick={() => setOpenTagLibraryModal(true)}
            >
              <Icon
                icon="majesticons:tag-line"
                style={{ width: '18px', height: '18px', color: '#303030' }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Upload data'} arrow>
            <IconButton
              size="small"
              sx={{
                border: '2px solid #e8e8e8',
                '&:hover': { border: '2px solid #303030' },
                mr: 0.5,
              }}
              onClick={onUploadClick}
            >
              <Icon
                icon="material-symbols:upload"
                style={{ width: '18px', height: '18px', color: '#303030' }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={'Download transcripts'} arrow>
            <IconButton
              size="small"
              sx={{
                border: '2px solid #e8e8e8',
                '&:hover': { border: '2px solid #303030' },
              }}
              onClick={() => setExportModalOpen(true)}
              disabled={isDownloading || transcripts.length === 0}
            >
              <Icon
                icon="material-symbols:download"
                style={{ width: '18px', height: '18px', color: '#303030' }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {(showFilters || hasActiveFilters) && (
        <>
          <TranscriptFilterByTag
            id={id}
            placeholder="Filter by tags"
            onFilterChange={onTagFilterChange}
            selectedFilters={selectedTags}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={statusFilter || ''}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.8rem', height: 32 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="closed">Complete</MenuItem>
                <MenuItem value="incomplete">Incomplete</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <Select
                value={typeFilter || ''}
                onChange={(e) => onTypeFilterChange(e.target.value)}
                displayEmpty
                sx={{ fontSize: '0.8rem', height: 32 }}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="primary">Primary</MenuItem>
                <MenuItem value="secondary">Secondary</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </>
      )}

      <Dialog open={exportModalOpen} onClose={closeExportModal} maxWidth="xs" fullWidth>
        <DialogTitle>Export transcripts</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {hasDerivedTranscripts && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Include
              </Typography>
              <RadioGroup value={scope} onChange={(e) => setScope(e.target.value)}>
                <FormControlLabel
                  value="all"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">All Transcripts</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Originals plus every Enhanced, Translated, and PII-Redacted version, grouped
                        per original.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5 }}
                />
                <FormControlLabel
                  value="original"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Original Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Just the raw recorded or uploaded transcripts. No derived versions.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start', mb: 0.5 }}
                />
                <FormControlLabel
                  value="derived"
                  control={<Radio size="small" />}
                  label={
                    <Box>
                      <Typography variant="body2">Derived Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Just the Enhanced, Translated, and PII-Redacted versions, grouped under each
                        original&apos;s name.
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2">Format</Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)}>
                <MenuItem value="txt">Text (.txt)</MenuItem>
                <MenuItem value="docx">Word (.docx)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeExportModal} disabled={isDownloading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDownload} disabled={isDownloading}>
            Download
          </Button>
        </DialogActions>
      </Dialog>

      {openTagLibraryModal && (
        <TagLibraryModal
          open={openTagLibraryModal}
          onClose={() => setOpenTagLibraryModal(false)}
          studyId={id}
        />
      )}
    </Box>
  );
};

export default TranscriptListHeader;
